import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	useContextMenuMESF,
	useHasPermission,
	useUserContext,
} from "@dexteel/mesf-core";
import {
	Alert,
	Box,
	Button,
	Grid2,
	MenuItem,
	Paper,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import type {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	MenuItemDef,
} from "ag-grid-community";
import AddIcon from "@mui/icons-material/Add";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { SearchTextField } from "@/controls/SearchTextField";
import type { Printer } from "./models";
import { KIND_COLOR, shownStatus, statusInfo } from "./statusInfo";
import { StatusDot } from "@/pages/workOrders/components/StatusCell";
import { usePrinters } from "./hooks/usePrinters";
import { useTableData } from "./hooks/useTableData";
import { PrinterDetailModal } from "./components/PrinterDetailModal";
import { PrinterFormModal } from "./components/PrinterFormModal";
import { liveDotSx } from "./components/StatusLabel";

const moment = getMomentTz();

export const PRINTERS_PERMISSION = "ui.pages.printers";

type Group = "all" | "ok" | "attention" | "off";
const GROUPS: { key: Exclude<Group, "all">; label: string; color: string }[] = [
	{ key: "ok", label: "Ready", color: KIND_COLOR.ok },
	{ key: "attention", label: "Need attention", color: KIND_COLOR.warn },
	{ key: "off", label: "Offline", color: KIND_COLOR.off },
];
const groupOf = (p: Printer): Exclude<Group, "all"> => {
	const k = statusInfo(shownStatus(p)).kind;
	return k === "ok" || k === "off" ? k : "attention";
};

const NewPrinterStatusBar = ({ onNew }: { onNew: () => void }) => (
	<Button
		variant="contained"
		color="primary"
		size="small"
		startIcon={<AddIcon />}
		onClick={onNew}
		sx={{ textTransform: "none", margin: "4px 8px" }}
	>
		NEW PRINTER
	</Button>
);

const PrintersContent = () => {
	const [error, setError] = useState("");
	const [search, setSearch] = useState("");
	const [detailId, setDetailId] = useState<number | null>(null);
	const [form, setForm] = useState<{ open: boolean; printer: Printer | null }>({
		open: false,
		printer: null,
	});
	const onError = (e: Error) => setError(e.message);

	const { data, isLoading, dataUpdatedAt } = usePrinters(onError);

	const [area, setArea] = useState("");
	const areaOf = useMemo(() => {
		const byAsset = new Map(
			(data?.stations ?? []).map((st) => [st.AssetId, st.AreaName]),
		);
		return (p: Printer) => byAsset.get(p.AssetId) ?? "";
	}, [data?.stations]);
	const all = data?.printers ?? [];
	const areas = [
		...new Set((data?.stations ?? []).map((st) => st.AreaName)),
	].sort();
	const inArea = area ? all.filter((p) => areaOf(p) === area) : all;
	const [group, setGroup] = useState<Group>("all");
	const counts = { all: inArea.length, ok: 0, attention: 0, off: 0 };
	for (const p of inArea) counts[groupOf(p)]++;
	const printers =
		group === "all" ? inArea : inArea.filter((p) => groupOf(p) === group);

	const { columnDefs, getMenuOptions } = useTableData({
		onDetails: (p) => setDetailId(p.PrinterId),
		onEdit: (p) => setForm({ open: true, printer: p }),
		onNew: () => setForm({ open: true, printer: null }),
	});

	const statusBar = useMemo(
		() => ({
			statusPanels: [
				{
					statusPanel: NewPrinterStatusBar,
					statusPanelParams: {
						onNew: () => setForm({ open: true, printer: null }),
					},
					align: "right" as const,
				},
			],
		}),
		[],
	);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			showContextMenu(event as any, params.node?.data, "printersContext");
			return [];
		},
		[showContextMenu],
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: register once
	useEffect(() => {
		registerConfig({ id: "printersContext", getOptions: getMenuOptions });
	}, []);

	const detail = all.find((p) => p.PrinterId === detailId) ?? null;

	return (
		<>
			<HelmetDexteel title="Printers" />
			<Grid2 container size={{ md: 12, xs: 12 }} spacing={1} p={1}>
				<Grid2
					size={{ md: 12, xs: 12 }}
					sx={{ display: "flex", alignItems: "baseline", gap: 1 }}
				>
					<Typography variant="h5" fontWeight={600}>
						Printers
					</Typography>
					{dataUpdatedAt > 0 && (
						<Typography
							variant="caption"
							sx={{ color: "#666", fontStyle: "italic" }}
						>
							Last updated at {moment(dataUpdatedAt).format("HH:mm:ss")}
						</Typography>
					)}
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper
						elevation={1}
						sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}
					>
						<Box sx={{ width: 280 }}>
							<SearchTextField
								value={search}
								onChange={setSearch}
								label="Search printers"
							/>
						</Box>
						<TextField
							select
							size="small"
							label="Area"
							value={area}
							onChange={(e) => setArea(e.target.value)}
							sx={{ width: 200 }}
							slotProps={{
								inputLabel: { shrink: true },
								select: { displayEmpty: true },
							}}
						>
							<MenuItem value="">All</MenuItem>
							{areas.map((a) => (
								<MenuItem key={a} value={a}>
									{a}
								</MenuItem>
							))}
						</TextField>
						<Box sx={{ flex: 1 }} />
						<ToggleButtonGroup
							exclusive
							size="small"
							color="primary"
							value={group}
							onChange={(_, v: Group | null) => v && setGroup(v)}
							sx={{
								"& .MuiToggleButton-root": { textTransform: "none", px: 1.5 },
							}}
						>
							<ToggleButton value="all">
								All<b style={{ marginLeft: 6 }}>{counts.all}</b>
							</ToggleButton>
							{GROUPS.map((g) => (
								<ToggleButton
									key={g.key}
									value={g.key}
									sx={g.key === "ok" ? liveDotSx : undefined}
								>
									<StatusDot color={g.color} />
									{g.label}
									<b style={{ marginLeft: 6 }}>{counts[g.key]}</b>
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</Paper>
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper elevation={2} sx={{ height: "68vh", width: "100%" }}>
						<AgGridReact<Printer>
							loading={isLoading}
							loadingOverlayComponent={CenteredLazyLoading}
							rowData={printers}
							columnDefs={columnDefs}
							rowHeight={34}
							headerHeight={30}
							animateRows
							quickFilterText={search}
							getRowId={(p) => String(p.data.PrinterId)}
							getContextMenuItems={getContextMenuItems}
							onRowClicked={(e) => {
								// row buttons have their own action
								if ((e.event?.target as Element | null)?.closest("button"))
									return;
								if (e.data) setDetailId(e.data.PrinterId);
							}}
							rowStyle={{ cursor: "pointer" }}
							tooltipShowDelay={300}
							suppressCellFocus
							statusBar={statusBar}
						/>
					</Paper>
				</Grid2>
			</Grid2>
			<PrinterDetailModal printer={detail} onHide={() => setDetailId(null)} />
			<PrinterFormModal
				open={form.open}
				printer={form.printer}
				stations={data?.stations ?? []}
				onHide={() => setForm({ open: false, printer: null })}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};

export const PrintersPage = () => {
	const hasPermission = useHasPermission();
	const { isLoading } = useUserContext();
	if (isLoading) return null;
	return hasPermission(PRINTERS_PERMISSION) ? (
		<PrintersContent />
	) : (
		<Alert severity="warning" sx={{ m: 3 }}>
			You do not have permission to access Printers.
		</Alert>
	);
};
